/**
 * The signed-in setup steps, moved out of AuthProvider.tsx as they were (a
 * pure move, 05 Oct 26, to stop that file growing past its 1527 lines; the
 * project rule is no code file past 1000). AuthProvider's ensureUserProfile
 * still starts them, once per user per load (profileSetupOnce.ts onceForUser),
 * from getSession and the auth events, in the same order as before:
 * profile, lead-gen invite, default workspace, fly_diy trial.
 *
 * Nothing here reads React state: each step is a function of the user, the
 * Supabase client and the browser storage, so the move changes no behaviour.
 */
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { noInviteKnown, rememberNoInvite } from './profileSetupOnce';

/** True when every step finished (or had nothing to do), false when one failed. */
export const runUserProfileSetup = async (user: User): Promise<boolean> => {
  try {
    console.log('Ensuring user profile for:', user.email);
    
    // First, try to get just the profile without joins (more reliable)
    const { data: existingProfile, error: fetchError } = await supabase
      .from('profiles')
      .select('id, email, full_name')
      .eq('id', user.id)
      .maybeSingle();

    if (!existingProfile) {
      console.log('Profile does not exist, creating...');
      if (fetchError) console.warn('Profile read failed, trying the insert anyway:', fetchError.message);
      let profileOk = true;
      
      // Profile doesn't exist, create one
      const { error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: user.id,
          email: user.email,
          full_name: user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0],
          avatar_url: user.user_metadata?.avatar_url || user.user_metadata?.picture,
          notification_preferences: {
            daily_reports: true,
            weekly_reports: true,
            monthly_reports: true,
            alerts: true,
            auto_respond: {
              enabled: false,
              respondToPositive: true,
              respondToNeutral: false,
              respondToNegative: true,
              minRating: 3
            }
          }
        });

      if (insertError) {
        // Check if it's a duplicate key error (profile already exists)
        if (insertError.code === '23505') {
          console.log('Profile already exists (race condition), continuing...');
        } else {
          console.error('Error creating user profile:', insertError);
          profileOk = false;
        }
      } else {
        console.log('User profile created successfully');
      }

      // Auto-accept any pending lead-gen invite for this user BEFORE
      // workspace creation — see autoAcceptPendingLeadGenInvite for why.
      // If it triggers a redirect, skip the rest (page is navigating).
      const inviteNew = await autoAcceptPendingLeadGenInvite(user);
      if (inviteNew === 'redirected') return true;

      // Create default workspace
      const workspaceNewOk = await ensureDefaultWorkspace(user);

      // Ensure user has a fly_diy subscription (creates trial if not exists)
      const subscriptionNewOk = await ensureFlyDiySubscription(user);
      return profileOk && inviteNew === 'done' && workspaceNewOk && subscriptionNewOk;
    } else {
      console.log('User profile already exists');

      // Auto-accept any pending lead-gen invite for this user BEFORE
      // workspace creation — see autoAcceptPendingLeadGenInvite for why.
      const inviteExisting = await autoAcceptPendingLeadGenInvite(user);
      if (inviteExisting === 'redirected') return true;

      // Check and create workspace if needed
      const workspaceOk = await ensureDefaultWorkspace(user);

      // Ensure user has a fly_diy subscription (creates trial if not exists)
      const subscriptionOk = await ensureFlyDiySubscription(user);
      return inviteExisting === 'done' && workspaceOk && subscriptionOk;
    }
  } catch (error) {
    console.error('Error ensuring user profile:', error);
    // Don't throw - let the user continue even if profile creation fails
    // The database trigger should handle it anyway. Report it, so the
    // next sign-in event on this load runs the setup again.
    return false;
  }
};

// Cache-proof safety net for the lead-gen invite acceptance flow.
//
// Background:
//  /accept-leads-generator-invitation is supposed to call the
//  leads-generator-accept-invite edge function after Google OAuth
//  completes. In practice this depends on the page's bundle making
//  the right calls in the right order — fragile when Safari Private
//  serves a stale JS bundle, or when the OAuth redirect_uri lands
//  the user back at /fly-app instead of the invite page. The result
//  is a user signed in with a `pending` invitation row, no agency,
//  and the full Fly sidebar.
//
//  This handler runs on EVERY sign-in (via ensureUserProfile) and
//  asks the DB "is there a pending lead-gen invite for my email?"
//  via a SECURITY DEFINER RPC. If yes, it fires the accept-invite
//  edge function with the returned token. The edge function does
//  the email-based auth lookup (PR #680) so it works regardless of
//  JWT timing, and creates the agency. AuthProvider then redirects
//  to /fly-app/lead-gen-tools so the sidebar/route reflect the new
//  state on the very next render.
//
//  Returns 'redirected' when the page has been redirected (so callers can
//  skip subsequent setup steps that would race the navigation), 'failed'
//  when the check or the accept failed (the setup runs again on the next
//  sign-in event), and 'done' otherwise.
export const autoAcceptPendingLeadGenInvite = async (_user: User): Promise<'redirected' | 'done' | 'failed'> => {
  try {
    // Skip on the AcceptLeadsGeneratorInvitation page — it runs its
    // own accept flow and we don't want to redirect out from under
    // the user's success screen.
    if (typeof window !== 'undefined' &&
        window.location.pathname.startsWith('/accept-leads-generator-invitation')) {
      return 'done';
    }

    // This tab already heard "no invite" (profileSetupOnce.ts says when it asks again).
    if (noInviteKnown(_user.id)) {
      console.log('[autoAcceptPendingLeadGenInvite] no pending invite already known this session, skipping the RPC');
      return 'done';
    }

    const { data: token, error: rpcError } = await supabase.rpc(
      'get_pending_lead_gen_invite_token_for_self'
    );
    if (rpcError) {
      console.warn('[autoAcceptPendingLeadGenInvite] RPC unavailable:', rpcError.message);
      return 'failed';
    }
    if (!token) {
      // No pending invite for this email — nothing to do.
      rememberNoInvite(_user.id);
      return 'done';
    }

    console.log('[autoAcceptPendingLeadGenInvite] pending invite found, firing accept');
    const { data, error } = await supabase.functions.invoke(
      'leads-generator-accept-invite',
      { body: { invitation_token: token } }
    );

    if (error) {
      console.error('[autoAcceptPendingLeadGenInvite] accept failed:', error);
      return 'failed';
    }

    // Idempotent — already_accepted=true means a previous run did the
    // work; no need to redirect (the user has already navigated past
    // the accept page in a prior session).
    if (data?.accepted && data?.already_accepted !== true) {
      console.log('[autoAcceptPendingLeadGenInvite] accepted; redirecting to lead-gen-tools');
      try {
        if (typeof window !== 'undefined') {
          const stale: string[] = [];
          for (let i = 0; i < window.localStorage.length; i++) {
            const k = window.localStorage.key(i);
            if (k && k.startsWith('flyLeadGenOnly:')) stale.push(k);
          }
          stale.forEach((k) => window.localStorage.removeItem(k));
          sessionStorage.removeItem('pending_lead_gen_invite_token');
        }
      } catch {
        // storage access denied — non-fatal
      }
      if (typeof window !== 'undefined') {
        // Hard-navigate (not React-router push) so every cache layer
        // — supabase-js session, useLeadGenOnly localStorage, AppSidebar
        // memoised state — gets re-read from scratch.
        if (!window.location.pathname.startsWith('/fly-app/lead-gen-tools')) {
          window.location.href = '/fly-app/lead-gen-tools';
        } else {
          window.location.reload();
        }
      }
      return 'redirected';
    }

    return 'done';
  } catch (err) {
    console.error('[autoAcceptPendingLeadGenInvite] unexpected error:', err);
    return 'failed';
  }
};

/** True when the user has a workspace or needs none, false when a step failed. */
export const ensureDefaultWorkspace = async (user: User): Promise<boolean> => {
  try {
    // Skip creating default workspace if user is accepting an invite —
    // they'll join the invited workspace / agency / lead-gen workspace
    // instead of needing their own.
    const pendingInviteToken = sessionStorage.getItem('pending_invite_token');
    const pendingAgencyInviteToken = sessionStorage.getItem('pending_agency_invite_token');
    // Lead-gen invitees never need a default workspace — they get a
    // `lead_gen_only=true` agency provisioned by the accept-invite
    // function instead. This sessionStorage flag is set by
    // AcceptLeadsGeneratorInvitation on mount and cleared after the
    // accept call completes. Without this skip, a fresh leads-gen
    // signup would race with the OAuth onAuthStateChange handler and
    // get a "shardul pujari's Workspace" auto-created on the side
    // (sometimes 2-3 of them as multiple SIGNED_IN events fire),
    // surfacing the full Fly nav for a user who only paid for lead-gen.
    const pendingLeadGenInviteToken = sessionStorage.getItem('pending_lead_gen_invite_token');
    if (pendingInviteToken || pendingAgencyInviteToken || pendingLeadGenInviteToken) {
      console.log('User has pending invite, skipping default workspace creation');
      return true;
    }
    // Belt-and-braces fallback: if the user already owns a
    // `lead_gen_only=true` agency, they're a leads-gen customer
    // regardless of sessionStorage state (e.g. they cleared storage
    // and signed in again later). Don't create a workspace for them.
    const { data: leadGenAgency } = await supabase
      .from('agencies')
      .select('id')
      .eq('owner_id', user.id)
      .eq('lead_gen_only', true)
      .limit(1)
      .maybeSingle();
    if (leadGenAgency) {
      console.log('User owns a lead_gen_only agency, skipping default workspace creation');
      return true;
    }

    // Check if user has any workspace
    const { data: workspaces, error: wsError } = await supabase
      .from('workspaces')
      .select('id')
      .eq('owner_id', user.id)
      .limit(1);

    if (!workspaces || workspaces.length === 0) {
      // Second belt-and-braces (asked only for a user with no workspace
      // of their own, since nothing is created otherwise; walk 05 Oct 26
      // item 9 saw it on every load): a fresh OAuth signup races the
      // accept-invite call. The sessionStorage flag set by
      // AcceptLeadsGeneratorInvitation can be cleared by React's
      // unmount cleanup mid-OAuth-roundtrip, AND the lead-gen
      // agency hasn't been provisioned yet (accept-invite runs
      // AFTER ensureUserProfile). Without this check the SIGNED_IN
      // handler creates a workspace seconds before the agency is
      // born, leaving the user looking like a hybrid (workspace +
      // agency) and surfacing the full Fly nav.
      //
      // The durable signal is the invitation row itself, keyed by
      // verified email. We can't query the table directly — RLS
      // hides pending rows (where accepted_user_id IS NULL) from
      // their own invitee. Instead call a SECURITY DEFINER RPC
      // that does the check server-side and returns a boolean,
      // bypassing RLS but only ever for the caller's own email.
      const { data: hasPendingInvite, error: rpcError } = await supabase
        .rpc('check_pending_lead_gen_invite_for_email');
      if (rpcError) {
        // RPC not deployed yet (migration pending) — fall through
        // to the existing logic. Don't block signups on a missing
        // safety net; the trigger-side handle_new_user fix is the
        // primary defense anyway.
        console.warn('check_pending_lead_gen_invite_for_email RPC unavailable:', rpcError.message);
      } else if (hasPendingInvite === true) {
        console.log('User has a pending/active Leads-Generator invite, skipping default workspace creation');
        return true;
      }

      console.log('No workspace found, creating default workspace...');

      const workspaceName = user.user_metadata?.full_name ||
                          user.user_metadata?.name ||
                          user.email?.split('@')[0] ||
                          'My';

      // Get agency_id from server-injected branding if on agency domain
      const agencyId = typeof window !== 'undefined' && window.__AGENCY_BRANDING__?.id
        ? window.__AGENCY_BRANDING__.id
        : null;

      if (agencyId) {
        console.log('Creating workspace with agency_id:', agencyId);
      }

      const { error: createError } = await supabase
        .from('workspaces')
        .insert({
          name: `${workspaceName}'s Workspace`,
          owner_id: user.id,
          agency_id: agencyId,
          settings: {
            multi_location_mode: false,
            auto_sync_enabled: true,
            sync_frequency: 'daily'
          }
        });
      
      if (createError) {
        // Check if it's a duplicate key error
        if (createError.code === '23505') {
          console.log('Workspace already exists (race condition), continuing...');
        } else {
          console.error('Error creating default workspace:', createError);
          return false;
        }
      } else {
        console.log('Default workspace created successfully');
      }
    } else {
      console.log('User already has a workspace');
    }
    return true;
  } catch (error) {
    console.error('Error ensuring default workspace:', error);
    // Don't throw - let the user continue
    return false;
  }
};

/** True when the subscription exists (or the user needs none), false when a step failed. */
export const ensureFlyDiySubscription = async (user: User): Promise<boolean> => {
  try {
    // Skip for internal users
    const email = user.email?.toLowerCase() || '';
    if (email.endsWith('@zoomlocal.in')) {
      console.log('Internal user, skipping subscription setup');
      return true;
    }

    // Use the database function to ensure subscription exists
    const { data, error } = await supabase.rpc('ensure_fly_diy_subscription', {
      p_user_id: user.id,
      p_trial_days: 14
    });

    if (error) {
      // If RPC doesn't exist yet (migration not run), fall back to direct insert
      if (error.code === '42883') {
        console.log('RPC not found, using direct subscription check...');

        // Check if subscription already exists
        const { data: existing } = await supabase
          .from('user_subscriptions')
          .select('id')
          .eq('user_id', user.id)
          .eq('product', 'fly_diy')
          .maybeSingle();

        if (!existing) {
          // Create trial subscription
          const trialEndsAt = new Date();
          trialEndsAt.setDate(trialEndsAt.getDate() + 14);

          const { error: insertError } = await supabase
            .from('user_subscriptions')
            .insert({
              user_id: user.id,
              product: 'fly_diy',
              status: 'trial',
              quantity: 1,
              trial_started_at: new Date().toISOString(),
              trial_ends_at: trialEndsAt.toISOString(),
              messages_limit: 1000,
              images_limit: 30
            });

          if (insertError && insertError.code !== '23505') {
            console.error('Error creating fly_diy subscription:', insertError);
            return false;
          } else {
            console.log('fly_diy trial subscription created');
          }
        } else {
          console.log('fly_diy subscription already exists');
        }
        return true;
      }
      console.error('Error ensuring fly_diy subscription:', error instanceof Error ? error.message : JSON.stringify(error));
      return false;
    }

    if (data && data.length > 0 && data[0].is_new) {
      console.log('fly_diy trial subscription created via RPC');
    } else {
      console.log('fly_diy subscription already exists');
    }
    return true;
  } catch (error) {
    console.error('Error in ensureFlyDiySubscription:', error);
    // Don't throw - let the user continue
    return false;
  }
};
